import { FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import TaxiCargoContractEditRouteParams from "../../common/routes/types/taxi-cargo-contract-edit-route-params";
import { useAppContext } from "../../../../context/app.context";
import appTranslationsHelper from "../../../../languages/app-translations.helper";
import useDocumentTitle from "../../../../common/hooks/use-document-title";
import taxiBreadcrumbsHelper from "../../common/breadcrumbs/taxi-breadcrumbs.helper";
import taxiTranslationsHelper from "../../../../languages/taxi-translations.helper";
import useForm from "../../../../common/components/form/use-form";
import TaxiCargoContractEditFormData from "./common/types/taxi-cargo-contract-edit-form-data";
import taxiCargoContractEditFormHelper from "./common/taxi-cargo-contract-edit-form.helper";
import HeadingComponent from "../../../../common/components/heading/heading.component";
import Row from "../../../../common/components/grid/row";
import Column from "../../../../common/components/grid/column";
import CardComponent from "../../../../common/components/card/card.component";
import FormFieldComponent from "../../../../common/components/form/field/form-field.component";
import BooleanSelectComponent from "../../../../common/components/form/select/boolean/boolean-select.component";
import ButtonComponent from "../../../../common/components/button/button.component";
import useContractsCargoTaxiDetails from "../../../../common/services/contracts/cargo-taxi/details/use-contracts-cargo-taxi-details";
import useAbort from "../../../../common/hooks/use-abort";
import notificationService from "../../../../common/utils/notification/notification.service";
import taxiRoutesHelper from "../../common/routes/taxi-routes.helper";
import booleanHelper from "../../../../common/utils/boolean/boolean.helper";
import useTaxiCorporationDetails from "../../../../common/services/taxi-corporation/details/use-taxi-corporation-details";
import NumericInputComponent from "../../../../common/components/form/input/numeric-input/numeric-input.component";
import taxiCargoContractEditFormFactory from "./common/taxi-cargo-contract-edit-form.factory";
import taxiCargoContractEditFormRequestFactory from "./common/taxi-cargo-contract-edit-params.factory";
import contractsService from "../../../../common/services/contracts/contracts.service";

type TaxiCargoContractEditProps = {};

const TaxiCargoContractEditComponent: FC<TaxiCargoContractEditProps> = () => {
  const navigate = useNavigate();

  const { taxiCorporationUuid, contractUuid } =
    useParams<TaxiCargoContractEditRouteParams>();
  const { setBreadcrumbs, selectedAppLanguage } = useAppContext();

  const documentTitleTranslations =
    appTranslationsHelper.getDocumentTitleTranslations();

  useDocumentTitle(documentTitleTranslations.taxiCargoContractEdit);

  const taxiDetails = useTaxiCorporationDetails();
  const taxiDetailsAbort = useAbort();

  useEffect(() => {
    if (!taxiCorporationUuid) {
      return;
    }

    taxiDetails.load(
      {
        taxiCorporationUuid,
      },
      taxiDetailsAbort.signal,
    );
  }, [taxiCorporationUuid]);

  useEffect(() => {
    const breadcrumbs = taxiBreadcrumbsHelper.getCargoContractEditBreadcrumbs({
      taxiCorporationUuid: taxiCorporationUuid!,
      contractUuid: contractUuid!,
      taxiCorporationDisplayName: taxiDetails.data?.displayName,
    });

    setBreadcrumbs(breadcrumbs);
  }, [selectedAppLanguage, taxiDetails.data]);

  const translations =
    taxiTranslationsHelper.getCargoContractEditTranslations();

  const form = useForm<TaxiCargoContractEditFormData>({
    emptyValues: taxiCargoContractEditFormHelper.getEmptyFormData(),
    validationDefinition:
      taxiCargoContractEditFormHelper.getValidationDefinition(),
  });

  const cargoTaxiContractDetails = useContractsCargoTaxiDetails();
  const cargoTaxiContractDetailsAbort = useAbort();

  useEffect(() => {
    if (!contractUuid) {
      return;
    }

    cargoTaxiContractDetails.load(
      {
        contractUuid,
      },
      cargoTaxiContractDetailsAbort.signal,
    );
  }, [contractUuid]);

  useEffect(() => {
    if (!cargoTaxiContractDetails.data) {
      return;
    }

    const formData = taxiCargoContractEditFormFactory.create(
      cargoTaxiContractDetails.data,
    );

    form.setValues(formData);
  }, [cargoTaxiContractDetails.data]);

  const cargoTaxiContractUpdateAbort = useAbort();

  const [isContractUpdatePending, setIsContractUpdatePending] = useState(false);

  const onContractUpdateSuccess = () => {
    notificationService.success(translations.successUpdateNotificationLabel);

    navigate(
      taxiRoutesHelper.getCargoContractListingRoute({
        taxiCorporationUuid: taxiCorporationUuid!,
      }),
    );
  };

  const onContractUpdateFailure = () => {
    notificationService.error(translations.failureUpdateNotificationLabel);
  };

  const updateContract = async () => {
    setIsContractUpdatePending(true);

    const params = taxiCargoContractEditFormRequestFactory.create(
      form.values,
      contractUuid!,
    );

    try {
      await contractsService.updateCargoTaxiContract(
        params,
        cargoTaxiContractUpdateAbort.signal,
      );

      onContractUpdateSuccess();
    } catch {
      onContractUpdateFailure();
    } finally {
      setIsContractUpdatePending(false);
    }
  };

  const onSubmitButtonClick = async () => {
    const isFormValid = await form.validateAll();

    if (!isFormValid) {
      return;
    }

    updateContract();
  };

  return (
    <>
      <HeadingComponent title={translations.header.headingLabel} />
      <Row>
        <Column xl={8}>
          <CardComponent isLoading={cargoTaxiContractDetails.isLoading}>
            <Row>
              <Column lg={6}>
                <FormFieldComponent label={translations.form.cargoCompanyLabel}>
                  {cargoTaxiContractDetails.data?.cargoCompany.displayName}
                </FormFieldComponent>
              </Column>
              <Column lg={6}>
                <FormFieldComponent
                  label={translations.form.periodOfValidityLabel}
                >
                  {taxiCargoContractEditFormHelper.getPeriodOfValidityLabel(
                    cargoTaxiContractDetails.data?.periodOfValidity,
                  )}
                </FormFieldComponent>
              </Column>
              <Column lg={6}>
                <FormFieldComponent label={translations.form.billingModelLabel}>
                  {cargoTaxiContractDetails.data?.billingModel}
                </FormFieldComponent>
              </Column>
              <Column lg={6}>
                <FormFieldComponent label={translations.form.distanceRateLabel}>
                  {translations.form.distanceRateContentLabel.replace(
                    "#{distanceRate}",
                    cargoTaxiContractDetails.data?.distanceRate.toString() ??
                      "",
                  )}
                </FormFieldComponent>
              </Column>
            </Row>
            <Row>
              <Column lg={6}>
                <FormFieldComponent
                  label={translations.form.discountPercentLimitLabel}
                >
                  {translations.form.discountPercentLimitContentLabel.replace(
                    "#{discountPercentLimit}",
                    cargoTaxiContractDetails.data?.discountPercentLimit.toString() ??
                      "",
                  )}
                </FormFieldComponent>
              </Column>
              <Column lg={6}>
                <FormFieldComponent
                  label={translations.form.freeStoppingPeriodLabel}
                >
                  {translations.form.freeStoppingPeriodContentLabel.replace(
                    "#{freeStoppingPeriod}",
                    cargoTaxiContractDetails.data?.freeStoppingPeriodMinutes.toString() ??
                      "",
                  )}
                </FormFieldComponent>
              </Column>
            </Row>
            <Row>
              <Column lg={6}>
                <FormFieldComponent label={translations.form.stoppingRateLabel}>
                  {translations.form.stoppingRateContentLabel.replace(
                    "#{stoppingRate}",
                    cargoTaxiContractDetails.data?.stoppingRate.toString() ??
                      "",
                  )}
                </FormFieldComponent>
              </Column>
              <Column lg={6}>
                <FormFieldComponent
                  label={translations.form.tollRoadsDuringOrderAllowedLabel}
                >
                  {booleanHelper.getLabel(
                    cargoTaxiContractDetails.data
                      ?.isTollRoadsDuringOrderAllowed!,
                  )}
                </FormFieldComponent>
              </Column>
            </Row>
            <Row>
              <Column lg={6}>
                <FormFieldComponent
                  label={
                    translations.form.tollRoadsWhileApproachingAllowedLabel
                  }
                >
                  {booleanHelper.getLabel(
                    cargoTaxiContractDetails.data
                      ?.isTollRoadsWhileArrivalAllowed!,
                  )}
                </FormFieldComponent>
              </Column>
              <Column lg={6}>
                <FormFieldComponent
                  label={translations.form.tollRoadsWhileReturningAllowedLabel}
                >
                  {booleanHelper.getLabel(
                    cargoTaxiContractDetails.data
                      ?.isTollRoadsWhileReturningAllowed!,
                  )}
                </FormFieldComponent>
              </Column>
            </Row>
            <Row>
              <Column lg={6}>
                <FormFieldComponent
                  label={translations.form.orderPublishingAllowedLabel}
                >
                  {booleanHelper.getLabel(
                    cargoTaxiContractDetails.data?.isOrderPublishingAllowed!,
                  )}
                </FormFieldComponent>
              </Column>
              <Column lg={6}>
                <FormFieldComponent
                  label={translations.form.contactPassengerAfterMinutesLabel}
                  isRequired
                  errorMessage={
                    form.validationResults.contactPassengerAfterMinutes
                      .errorMessage
                  }
                >
                  <NumericInputComponent
                    value={form.values.contactPassengerAfterMinutes}
                    placeholder={
                      translations.form.contactPassengerAfterMinutesPlaceholder
                    }
                    onChange={(value) =>
                      form.setValue("contactPassengerAfterMinutes", value)
                    }
                    hasError={
                      !!form.validationResults.contactPassengerAfterMinutes
                        .errorMessage
                    }
                    onBlur={() => form.validate("contactPassengerAfterMinutes")}
                    isIntegerOnly
                    idForTesting="taxi-cargo-contract-edit-contact-passenger-after-minutes"
                  />
                </FormFieldComponent>
              </Column>
            </Row>
            <Row>
              <Column lg={6}>
                <FormFieldComponent
                  label={translations.form.cancelOrderByTaxiAllowedLabel}
                  isRequired
                  errorMessage={
                    form.validationResults.cancelOrderByTaxiAllowed.errorMessage
                  }
                >
                  <BooleanSelectComponent
                    placeholder={
                      translations.form.cancelOrderByTaxiAllowedPlaceholder
                    }
                    value={form.values.cancelOrderByTaxiAllowed}
                    onChange={(value) =>
                      form.setValue("cancelOrderByTaxiAllowed", value)
                    }
                    isError={
                      !!form.validationResults.cancelOrderByTaxiAllowed
                        .errorMessage
                    }
                    onBlur={() => form.validate("cancelOrderByTaxiAllowed")}
                    idForTesting="taxi-cargo-contract-edit-cancel-order-by-taxi-allowed"
                  />
                </FormFieldComponent>
              </Column>
              <Column lg={6}>
                <FormFieldComponent
                  label={translations.form.editOrderByTaxiAllowedLabel}
                  isRequired
                  errorMessage={
                    form.validationResults.editOrderByTaxiAllowed.errorMessage
                  }
                >
                  <BooleanSelectComponent
                    placeholder={translations.form.editOrderByTaxiAllowedLabel}
                    value={form.values.editOrderByTaxiAllowed}
                    onChange={(value) =>
                      form.setValue("editOrderByTaxiAllowed", value)
                    }
                    isError={
                      !!form.validationResults.editOrderByTaxiAllowed
                        .errorMessage
                    }
                    onBlur={() => form.validate("editOrderByTaxiAllowed")}
                    idForTesting="taxi-cargo-contract-edit-edit-order-by-taxi-allowed"
                  />
                </FormFieldComponent>
              </Column>
            </Row>
            <Row>
              <Column lg={12}>
                <FormFieldComponent
                  label={translations.form.isActiveLabel}
                  isRequired
                  errorMessage={form.validationResults.isActive.errorMessage}
                >
                  <BooleanSelectComponent
                    placeholder={translations.form.isActivePlaceholder}
                    value={form.values.isActive}
                    onChange={(value) => form.setValue("isActive", value)}
                    isError={!!form.validationResults.isActive.errorMessage}
                    onBlur={() => form.validate("isActive")}
                    idForTesting="taxi-cargo-contract-edit-is-active"
                  />
                </FormFieldComponent>
              </Column>
            </Row>
          </CardComponent>
          <CardComponent
            isLoading={cargoTaxiContractDetails.isLoading}
            header={{ title: translations.form.autoApprovalHeaderTitle }}
          >
            <Row>
              <Column lg={6}>
                <FormFieldComponent
                  label={translations.form.autoApprovalSwitchLabel}
                  isRequired
                  errorMessage={
                    form.validationResults.autoApprovalSwitch.errorMessage
                  }
                >
                  <BooleanSelectComponent
                    placeholder={
                      translations.form.autoApprovalSwitchPlaceholder
                    }
                    value={form.values.autoApprovalSwitch}
                    onChange={(value) =>
                      form.setValue("autoApprovalSwitch", value)
                    }
                    isError={
                      !!form.validationResults.autoApprovalSwitch.errorMessage
                    }
                    onBlur={() => {
                      form.validate("autoApprovalSwitch");
                      form.validate("autoApprovalMinDistance");
                      form.validate("autoApprovalMinTime");
                    }}
                    idForTesting="taxi-cargo-contract-edit-auto-approval-switch"
                  />
                </FormFieldComponent>
              </Column>
              <Column lg={6}>
                <FormFieldComponent
                  label={translations.form.autoApprovalMinDistanceLabel}
                  isRequired={!!form.values.autoApprovalSwitch}
                  errorMessage={
                    form.validationResults.autoApprovalMinDistance.errorMessage
                  }
                >
                  <NumericInputComponent
                    value={form.values.autoApprovalMinDistance}
                    placeholder={
                      translations.form.autoApprovalMinDistancePlaceholder
                    }
                    onChange={(value) =>
                      form.setValue("autoApprovalMinDistance", value)
                    }
                    hasError={
                      !!form.validationResults.autoApprovalMinDistance
                        .errorMessage
                    }
                    onBlur={() => form.validate("autoApprovalMinDistance")}
                    isDisabled={!form.values.autoApprovalSwitch}
                    isIntegerOnly
                    idForTesting="taxi-cargo-contract-edit-auto-approval-min-distance"
                  />
                </FormFieldComponent>
              </Column>
            </Row>
            <Row>
              <Column lg={6}>
                <FormFieldComponent
                  label={translations.form.autoApprovalMinTimeLabel}
                  isRequired={!!form.values.autoApprovalSwitch}
                  errorMessage={
                    form.validationResults.autoApprovalMinTime.errorMessage
                  }
                >
                  <NumericInputComponent
                    value={form.values.autoApprovalMinTime}
                    placeholder={
                      translations.form.autoApprovalMinTimePlaceholder
                    }
                    onChange={(value) => {
                      form.setValue("autoApprovalMinTime", value);
                    }}
                    hasError={
                      !!form.validationResults.autoApprovalMinTime.errorMessage
                    }
                    onBlur={() => form.validate("autoApprovalMinTime")}
                    isDisabled={!form.values.autoApprovalSwitch}
                    isIntegerOnly
                    idForTesting="cargo-taxi-contract-add-auto-approval-min-time"
                  />
                </FormFieldComponent>
              </Column>
            </Row>
          </CardComponent>
          <CardComponent
            header={{ title: translations.form.approvalDeadlineHeaderTitle }}
          >
            <Row>
              <Column lg={6}>
                <FormFieldComponent
                  label={translations.form.approvalDeadlineSwitchLabel}
                  isRequired
                  errorMessage={
                    form.validationResults.approvalDeadlineSwitch.errorMessage
                  }
                >
                  <BooleanSelectComponent
                    placeholder={
                      translations.form.approvalDeadlineSwitchPlaceholder
                    }
                    value={form.values.approvalDeadlineSwitch}
                    onChange={(value) =>
                      form.setValue("approvalDeadlineSwitch", value)
                    }
                    isError={
                      !!form.validationResults.approvalDeadlineSwitch
                        .errorMessage
                    }
                    onBlur={() => {
                      form.validate("approvalDeadlineSwitch");
                      form.validate("approvalDeadlineTime");
                    }}
                    idForTesting="cargo-taxi-contract-add-approval-deadline-switch"
                  />
                </FormFieldComponent>
              </Column>
              <Column lg={6}>
                <FormFieldComponent
                  label={translations.form.approvalDeadlineTimeLabel}
                  isRequired={!!form.values.approvalDeadlineSwitch}
                  errorMessage={
                    form.validationResults.approvalDeadlineTime.errorMessage
                  }
                >
                  <NumericInputComponent
                    value={form.values.approvalDeadlineTime}
                    placeholder={
                      translations.form.approvalDeadlineTimePlaceholder
                    }
                    onChange={(value) => {
                      form.setValue("approvalDeadlineTime", value);
                    }}
                    hasError={
                      !!form.validationResults.approvalDeadlineTime.errorMessage
                    }
                    onBlur={() => form.validate("approvalDeadlineTime")}
                    isDisabled={!form.values.approvalDeadlineSwitch}
                    isIntegerOnly
                    idForTesting="cargo-taxi-contract-add-approval-deadline-time"
                  />
                </FormFieldComponent>
              </Column>
            </Row>
          </CardComponent>
          <ButtonComponent
            onClick={onSubmitButtonClick}
            type="primary"
            isLoading={isContractUpdatePending}
            idForTesting="taxi-cargo-contract-edit-submit-button"
            classNames={{ root: "mt-4" }}
            title={translations.form.submitButtonTitle}
          >
            {translations.form.submitButtonLabel}
          </ButtonComponent>
        </Column>
      </Row>
    </>
  );
};

export default TaxiCargoContractEditComponent;
